import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { FaWhatsapp, FaTools } from 'react-icons/fa';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import { SettingsProvider, useSettings } from './context/SettingsContext';

// Lazy-loaded Public Pages
const Services = lazy(() => import('./pages/Services'));
const Courses = lazy(() => import('./pages/Courses'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const CourseLMSViewerPage = lazy(() => import('./pages/CourseLMSViewerPage'));
const StudentMyLearningPage = lazy(() => import('./pages/StudentMyLearningPage'));
const Tracks = lazy(() => import('./pages/Tracks'));
const DiplomaDetail = lazy(() => import('./pages/DiplomaDetail'));
const Webinars = lazy(() => import('./pages/Webinars'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const PublicCertificateVerificationPage = lazy(() => import('./pages/PublicCertificateVerificationPage'));
const StudentCertificatesPage = lazy(() => import('./pages/StudentCertificatesPage'));
const CorporateRequestPage = lazy(() => import('./pages/CorporateRequestPage'));
const CompanyLoginPage = lazy(() => import('./pages/CompanyLoginPage'));
const CompanyPortalPage = lazy(() => import('./pages/CompanyPortalPage'));
const CorporateTraineePortalPage = lazy(() => import('./pages/CorporateTraineePortalPage'));
const CorporateTraineeLoginPage = lazy(() => import('./pages/CorporateTraineeLoginPage'));

// External Admin Dashboard Redirector
const AdminRedirect = () => {
  useEffect(() => {
    const adminUrl = process.env.REACT_APP_ADMIN_URL || (window.location.hostname.includes('localhost') ? 'http://localhost:5173' : 'https://admin.datxsci.com');
    window.location.replace(adminUrl);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8 text-center" dir="rtl">
      <div className="w-12 h-12 rounded-2xl border-2 border-cyan-500/20 border-t-cyan-500 animate-spin mb-4" />
      <span className="text-sm font-semibold text-cyan-300">جاري التحويل إلى لوحة التحكم الإدارية المستقلة...</span>
    </div>
  );
};

// Sleek Brand Loader for Suspense
const PageLoader = () => (
  <div className="min-h-[50vh] flex flex-col items-center justify-center p-8 text-center" dir="rtl">
    <div className="w-10 h-10 rounded-2xl border-2 border-blue-500/20 border-t-blue-500 animate-spin mb-3" />
    <span className="text-xs font-medium text-blue-200/60">جاري تحميل المحتوى...</span>
  </div>
);

// Global Scroll To Top & Dynamic SEO Page Title on Route Navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // Dynamic Title Mapping for SEO & Brand Recognition
    const titles = {
      '/': 'DATX | داتكس - علوم البيانات والذكاء الاصطناعي',
      '/tracks': 'المسارات التعليمية والدبلومات المتخصصة | DATX داتكس',
      '/courses': 'الدورات التدريبية في علوم البيانات والذكاء الاصطناعي | DATX داتكس',
      '/blog': 'المدونة والمقالات التقنية المتخصصة | DATX داتكس',
      '/seminars': 'الندوات وورش العمل المباشرة | DATX داتكس',
      '/webinars': 'الندوات وورش العمل المباشرة | DATX داتكس',
      '/about': 'عن داتكس ورؤيتنا | DATX',
      '/services': 'حلول وخدمات قطاع الأعمال والشركات | DATX داتكس',
      '/contact': 'تواصل معنا | DATX داتكس',
      '/verify': 'التحقق من صحة الشهادات المعتمدة | DATX داتكس',
      '/corporate-request': 'طلب تدريب وتأهيل الكوادر للمؤسسات | DATX داتكس',
      '/my-learning': 'لوحة تعلّمي ومساراتي | DATX داتكس',
      '/student/certificates': 'شهاداتي المعتمدة | DATX داتكس',
    };

    if (titles[pathname]) {
      document.title = titles[pathname];
    } else if (!pathname.startsWith('/course/') && !pathname.startsWith('/blog/') && !pathname.startsWith('/diploma/')) {
      document.title = 'DATX | داتكس - علوم البيانات والذكاء الاصطناعي';
    }
  }, [pathname]);

  return null;
};

// Floating WhatsApp Quick Chat Button
const FloatingWhatsAppButton = ({ whatsappNumber, defaultMessage, show }) => {
  if (!show || !whatsappNumber) return null;
  const cleanPhone = whatsappNumber.replace(/[^0-9]/g, '');
  const encodedMsg = encodeURIComponent(defaultMessage || 'مرحباً، أود الاستفسار عن برامج ودورات داتكس DATX');
  const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMsg}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-[max(2rem,env(safe-area-inset-bottom))] left-6 sm:left-8 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300 z-50 group"
      style={{ backgroundColor: '#25D366' }}
      aria-label="تواصل عبر الواتساب"
    >
      <FaWhatsapp className="text-3xl text-white" />
    </a>
  );
};

// Public Layout Wrapper with Navbar & Footer
const PublicLayout = () => {
  const { settings, isVisible } = useSettings();
  const isMaintenance = isVisible('maintenance_mode', false);

  if (isMaintenance) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-6 text-center font-arabic relative overflow-hidden" dir="rtl">
        <div className="absolute inset-0 bg-gradient-to-tr from-cyan-900/20 via-slate-900/50 to-blue-900/20 pointer-events-none" />
        <div className="relative z-10 max-w-xl bg-slate-900/80 border border-slate-800 p-8 sm:p-12 rounded-3xl backdrop-blur-xl shadow-2xl space-y-6">
          <div className="w-20 h-20 mx-auto bg-amber-500/10 border border-amber-500/30 rounded-3xl flex items-center justify-center text-amber-400 text-3xl shadow-lg shadow-amber-500/10">
            <FaTools className="animate-spin text-4xl" style={{ animationDuration: '6s' }} />
          </div>
          <h1 className="text-3xl font-extrabold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            {settings?.site_name_ar || 'داتكس DATX'}
          </h1>
          <h2 className="text-xl font-bold text-white">النظام قيد الصيانة والتحديث الدوري</h2>
          <p className="text-slate-300 text-sm leading-relaxed font-light">
            {settings?.maintenance_message || 'نقوم حالياً بإجراء تحديثات هامة لتحسين تجربتكم وتطوير الخدمات. سنعود للعمل بكامل طاقتنا في أقرب وقت!'}
          </p>
          <div className="pt-4 border-t border-slate-800 text-xs text-slate-500">
            إذا كنت من فريق الإدارة، يمكنك{' '}
            <a 
              href={process.env.REACT_APP_ADMIN_URL || (window.location.hostname.includes('localhost') ? 'http://localhost:5173' : 'https://admin.datxsci.com')} 
              className="text-cyan-400 hover:underline font-bold"
            >
              الدخول إلى لوحة التحكم الإدارية
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen font-arabic relative">
      <Navbar />
      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <FloatingWhatsAppButton
        whatsappNumber={settings?.whatsapp_number}
        defaultMessage={settings?.whatsapp_default_message}
        show={isVisible('show_whatsapp_button', true)}
      />
    </div>
  );
};

function App() {
  useEffect(() => {
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'ar';
  }, []);

  return (
    <SettingsProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* 🌐 مسارات الموقع العام للمستفيدين */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:courseId" element={<CourseDetail />} />
            <Route path="/courses/:category/:course" element={<CourseDetail />} />
            <Route path="/courses/:courseId/learn" element={<CourseLMSViewerPage />} />
            <Route path="/my-learning" element={<StudentMyLearningPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/tracks" element={<Tracks />} />
            <Route path="/diplomas" element={<Tracks />} />
            <Route path="/tracks/:trackId" element={<DiplomaDetail />} />
            <Route path="/diplomas/:diplomaId" element={<DiplomaDetail />} />
            <Route path="/webinars" element={<Webinars />} />
            <Route path="/nawat" element={<Webinars />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/verify" element={<PublicCertificateVerificationPage />} />
            <Route path="/verify/:code" element={<PublicCertificateVerificationPage />} />
            <Route path="/my-certificates" element={<StudentCertificatesPage />} />
            <Route path="/dashboard/certificates" element={<StudentCertificatesPage />} />
            
            {/* 🏢 مسارات الحلول المؤسسية والشركات */}
            <Route path="/corporate/request" element={<CorporateRequestPage />} />
            <Route path="/company/login" element={<CompanyLoginPage />} />
            <Route path="/company/portal" element={<CompanyPortalPage />} />
            <Route path="/company/portal/:slug" element={<CompanyPortalPage />} />
            <Route path="/trainee/login" element={<CorporateTraineeLoginPage />} />
            <Route path="/corporate/trainee/login" element={<CorporateTraineeLoginPage />} />
            <Route path="/trainee/portal" element={<CorporateTraineePortalPage />} />
            <Route path="/trainee/portal/:slug" element={<CorporateTraineePortalPage />} />
            <Route path="/corporate/trainee/portal" element={<CorporateTraineePortalPage />} />
            <Route path="/corporate/trainee/portal/:slug" element={<CorporateTraineePortalPage />} />
          </Route>

          {/* 🔄 إعادة توجيه مسارات الإدارة القديمة إلى لوحة التحكم الحديثة المستقلة */}
          <Route path="/admin/login" element={<AdminRedirect />} />
          <Route path="/admin/*" element={<AdminRedirect />} />
          <Route path="/admin" element={<AdminRedirect />} />
        </Routes>
      </Router>
    </SettingsProvider>
  );
}

export default App;